import { DateTime } from 'luxon'
import de from '@/locales/de'
import type { TermsConfig } from '@/types'
import { safeSessionStorage } from '@/composables/useSSR'

/**
 * The terms bundle: the namespaces one version id covers. A change to any of
 * them is a new `termsPage.version`; their own `version` fields stay display
 * dates.
 */
export const TERMS_BUNDLE_NAMESPACES = ['termsPage', 'nis2Policy', 'sla', 'withdrawal'] as const

/**
 * The version the bundled text carries, sent with every declaration. Read from
 * the German locale because German is authoritative and this must name the
 * exact wording shown.
 */
export const BUNDLE_TERMS_VERSION: string = de.termsPage.version

/**
 * Marks a bundled string whose wording the legal side has not supplied yet.
 * Marked strings never enter an archived version, and the accept page offers
 * nothing while the declaration it would show carries the marker.
 */
export const TERMS_PLACEHOLDER_MARKER = 'LEGAL PLACEHOLDER:'

export type FlatBundle = Record<string, string>
export interface TermsSnapshot {
  de: FlatBundle
  en: FlatBundle
}
export type TermsLocale = keyof TermsSnapshot

export const isPlaceholder = (value: string | undefined): boolean =>
  typeof value === 'string' && value.startsWith(TERMS_PLACEHOLDER_MARKER)

const flattenInto = (node: unknown, path: string, out: FlatBundle) => {
  if (typeof node === 'string') {
    if (!isPlaceholder(node)) out[path] = node
  } else if (Array.isArray(node)) {
    node.forEach((child, i) => flattenInto(child, `${path}[${i}]`, out))
  } else if (node && typeof node === 'object') {
    for (const [key, child] of Object.entries(node)) flattenInto(child, `${path}.${key}`, out)
  }
}

/** One locale's bundle as an archived version stores it: raw message source, placeholders left out. */
export const flattenBundle = (messages: Record<string, unknown>): FlatBundle => {
  const out: FlatBundle = {}
  for (const ns of TERMS_BUNDLE_NAMESPACES) flattenInto(messages[ns], ns, out)
  return out
}

const snapshotLoaders = import.meta.glob<TermsSnapshot>('./termsArchive/*.json', { import: 'default' })
const snapshotPath = (id: string) => `./termsArchive/${id}.json`

export const hasSnapshot = (id: string): boolean => snapshotPath(id) in snapshotLoaders

export const loadSnapshot = async (id: string): Promise<TermsSnapshot | null> => {
  const load = snapshotLoaders[snapshotPath(id)]
  return load ? load() : null
}

/**
 * Whether this bundle's text was replaced by a newer published version. A
 * version the server does not list counts as newer, so a frontend deployed
 * ahead of its registry entry never prompts a reload.
 */
export const isBundleStale = (config: TermsConfig, bundle: string = BUNDLE_TERMS_VERSION): boolean => {
  const ids = config.versions.map((v) => v.id)
  const bundleSeq = ids.indexOf(bundle)
  return bundleSeq !== -1 && bundleSeq < ids.indexOf(config.current_version)
}

/** "Fassung vom ...": a version id starts with its publication date. */
export const formatVersionDate = (id: string, locale: string): string => {
  const date = DateTime.fromISO(id.slice(0, 10))
  return date.isValid ? date.setLocale(locale).toLocaleString(DateTime.DATE_FULL) : id
}

const RELOAD_GUARD_KEY = 'termsReloadedFor'

/**
 * Reloads once to fetch the build carrying `version`. A second request for
 * the same version returns false instead, so a deployment still serving the
 * old build cannot loop.
 */
export const reloadOnceFor = (version: string): boolean => {
  const storage = safeSessionStorage()
  try {
    if (!storage || storage.getItem(RELOAD_GUARD_KEY) === version) return false
    storage.setItem(RELOAD_GUARD_KEY, version)
  } catch {
    return false
  }
  window.location.reload()
  return true
}

/** The message key a version's change summary sits under in termsChanges. */
export const termsSummaryKey = (id: string): string => `v${id.replace(/[^0-9A-Za-z]/g, '_')}`

/**
 * Versions after `from` up to and including `to`, in published order: the
 * changes a comparison between the two spans. Without the server's list only
 * `to` is known.
 */
export const versionsBetween = (config: TermsConfig | null, from: string | null, to: string): string[] => {
  const ids = config?.versions.map((v) => v.id) ?? []
  const end = ids.indexOf(to)
  if (end === -1) return [to]
  return ids.slice(from === null ? 0 : ids.indexOf(from) + 1, end + 1)
}
